import { Alert, Box, Dialog, Stack } from "@mui/material";
import api from "app/api";
import {
  Button,
  DialogTitle,
  Field,
  FieldsContainer,
  Form,
  Spacer,
  TextField,
  Toolbar,
} from "common";
import FormEditorForm from "components/FormEditor/Form";
import FormEditorDataProvider from "components/FormEditor/FormEditorDataProvider";
import Templates from "components/FormEditor/Templates";
import DragDropFile from "components/PdfFileUpload/DragDropFile";
import I18n from "i18n-js";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import WaiverPreview from "./WaiverPreview";
import { WaiverContextType } from "./WaiverProvider";
import { UseWaiverFormProps } from "./useWaiverForm";
export interface WaiverFormProps extends UseWaiverFormProps {}

export default function WaiverForm({
  id,
  afterSave,
  onClose,
  listItem,
}: WaiverFormProps) {
  const {
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    setAttributes,
    saving,
    service,
    previewOpen,
    setPreviewOpen,
    previewDialog,
    sharingOpen,
    setSharingOpen,
    sharingDialog,
    error,
    setError,
    setErrors,
    saveAndPublish,
    params,
  } =
    useOutletContext<WaiverContextType>(/*{
    id,
    afterSave,
  }*/);
  const navigate = useNavigate();
  const title = isCreate
    ? I18n.t("Waiver.addTitle")
    : I18n.t("Waiver.editTitle");

  const scroller = useRef<null | HTMLDivElement>(null);
  const isScrolling = useRef<any>();
  const { uploadFile } = api.getService("/files");

  const handleScroll = useCallback((e: any) => {
    // Clear our timeout throughout the scroll
    //console.log(window.scrollY);
    const topDelta = 164;
    if (scroller.current) {
      if (window.scrollY > topDelta) {
        scroller.current.style.marginTop = `${window.scrollY - topDelta}px`;
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll, false);
    return () => document.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <FormEditorDataProvider
      type={item.type}
      value={item.fields}
      onChange={evt => {
        setAttributes({ fields: evt.target?.value });
      }}
    >
      {/*<PageHeader
        title={title}
        onClose={onClose}
        sx={{ marginTop: -3 }}
        backUrl=".."
      >
        <MuiButton
          title={I18n.t("Waiver.preview")}
          startIcon={<VisibilityIcon fontSize="small" />}
          onClick={() => setPreviewOpen(true)}
        >
          {I18n.t("Waiver.preview")}
        </MuiButton>

        <FormEditorMenu waiverId={item.id} />
      </PageHeader>*/}
      <Stack direction="row" spacing={0}>
        <Box sx={{ flex: 1, paddingRight: 3 }}>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="Waiver.attributes"
            translationPlaceholdersCategory="Waiver.placeholders"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
          >
            <FieldsContainer spacing={2}>
              <Field fullWidth>
                <TextField id="name" />
              </Field>
              <Field fullWidth>
                <TextField id="description" multiline minRows={3} />
              </Field>
              {item.type === "file" && (
                <Field fullWidth>
                  <DragDropFile
                    id="file"
                    upload={uploadFile}
                    value={item.file?.file_name}
                    uri={item.file?.url}
                  />
                </Field>
              )}
            </FieldsContainer>
            {!!item.fields && <Spacer h={2} />}
            <FormEditorForm />
            {!!errors?.fields && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() =>
                  setErrors({ ...errors, fields: undefined as any })
                }
              >
                {errors?.fields}
              </Alert>
            )}
            {!!error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setError(undefined)}
              >
                {error}
              </Alert>
            )}
            {(!item.fields || item.fields.length === 0) && (
              <>
                <Spacer h={2} />
                <Alert severity="info">
                  {I18n.t("FormEditor.templates.info")}
                </Alert>
              </>
            )}
          </Form>
          <Spacer h={2} />
          <Toolbar sx={{ gap: 2 }}>
            <Spacer right />
            <Button
              title={I18n.t("Waiver.saveAsDraft")}
              disabled={!isDirty}
              onClick={() => save()}
              loading={saving}
            />
            <Button
              title={I18n.t("Waiver.saveAndPublish")}
              disabled={!isDirty}
              onClick={() => saveAndPublish()}
              loading={saving}
            />
          </Toolbar>
        </Box>
        <Box ref={scroller}>
          <Templates />
        </Box>
      </Stack>
      <Dialog {...previewDialog} maxWidth="xl">
        <DialogTitle onClose={previewDialog.onClose}>Preview</DialogTitle>
        <WaiverPreview item={item} />
      </Dialog>
    </FormEditorDataProvider>
  );
}
